import React, { useState } from 'react';
import { ShieldQuestion, X } from 'lucide-react';
import { buildReviewNotice } from '@/lib/whatsapp/accountReview';

interface AccountReviewBannerProps {
  reviewDecision?: string | null;
  accountName?: string | null;
  phoneNumber?: string | null;
  onDismiss?: () => void;
}

const TONE = {
  shell: 'border-[#FEDF89] bg-[#FFFAEB]',
  chip: 'bg-[#FEF0C7] text-[#B54708]',
  headline: 'text-[#93370D]',
  body: 'text-[#B54708]',
  dismiss: 'text-[#B54708] hover:bg-[#FEF0C7]',
} as const;

export const AccountReviewBanner: React.FC<AccountReviewBannerProps> = ({
  reviewDecision,
  accountName,
  phoneNumber,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);

  const notice = buildReviewNotice(reviewDecision);

  if (!notice) return null;
  if (dismissed) return null;

  const label = [accountName, phoneNumber ? `(${phoneNumber})` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`z-50 flex w-full items-start justify-between gap-3 border-b px-4 py-2.5 ${TONE.shell}`}
      data-testid="account-review-banner"
      data-known={String(notice.known)}
      role="status"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <span
          className={`mt-px inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${TONE.chip}`}
        >
          <ShieldQuestion className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
        </span>
        <div className="min-w-0">
          <p
            className={`truncate text-[13px] font-semibold leading-5 ${TONE.headline}`}
            data-testid="account-review-banner-headline"
          >
            {label ? `${label} — ${notice.title}` : notice.title}
          </p>
          <p
            className={`line-clamp-2 h-8 text-[11px] font-medium leading-4 ${TONE.body}`}
            data-testid="account-review-banner-detail"
          >
            {notice.detail}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          data-testid="account-review-banner-dismiss"
          aria-label="Dismiss"
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${TONE.dismiss}`}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default AccountReviewBanner;
